import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSkipSummary,
  fetchSkipFilings,
  fetchFiling,
  toggleLiverun,
} from "../reducers/fullSkipAnalysisSlice";
import LabFilingDetails from "./LabFilingDetails";

const TABLE_DAYDATA_COLUMNS = [
  {
    field: "license_email",
    headerName: "License Email",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "emis_id",
    headerName: "EMIS ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "lab_report_id",
    headerName: "Lab Report Id",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "category",
    headerName: "Category",
    flex: 2,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "category_type",
    headerName: "Report Type",
    flex: 2,
    align: "center",
    headerAlign: "center",
    renderCell: (params) =>
      params.value ? "Normal Report" : "Abnormal Report",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "starttime",
    headerName: "Start Time",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "notes",
    headerName: "Notes",
    flex: 4,
    align: "center",
    headerAlign: "center",
  },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (d) =>
  new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

const FullSkipFrame = () => {
  const dispatch = useDispatch();
  const {
    summary,
    filings,
    lab,
    liverun,
    loadingSummary,
    loadingFilings,
    loadingLabReport,
  } = useSelector((s) => s.fullSkipAnalysis);

  const access_token = useSelector((s) => s.authen.accessToken);

  const [openFilingDialog, setOpenFilingDialog] = useState(false);

  const [startDate, setStartDate] = useState(daysAgoISO(60));
  const [endDate, setEndDate] = useState(todayISO());

  useEffect(() => {
    if (access_token) {
      dispatch(
        fetchSkipSummary({
          start_date: startDate,
          end_date: endDate,
          liverun,
          access_token,
        }),
      );
    }
  }, [startDate, endDate, liverun, access_token, dispatch]);

  const handleSummaryRowClick = (params) => {
    dispatch(
      fetchSkipFilings({
        start_date: startDate,
        end_date: endDate,
        license_email: params.row.license_email,
        category_names: params.row.category_names,
        subtest_names: params.row.subtest_names,
        skip_type: params.row.skip_type,
        liverun: true,
        access_token,
      }),
    );
  };

  const handleOpenFiling = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    const license_email = params.row?.license_email;

    dispatch(
      fetchFiling({ license_email, lab_report_id, liverun, access_token }),
    );
    setOpenFilingDialog(true);
  };

  const handleCloseFilingDialog = () => {
    setOpenFilingDialog(false);
  };

  return (
    <Box p={2}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Full Skip Analysis</Typography>

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <TextField
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <FormControlLabel
            sx={{ m: 0, ml: "auto" }}
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={liverun}
                onChange={() => dispatch(toggleLiverun())}
                color="success"
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: liverun ? "#2f855a" : "#c53030" }}
              >
                {liverun ? "Live Run" : "Dry Run"}
              </Typography>
            }
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        {loadingSummary ? (
          <CircularProgress />
        ) : (
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rowHeight={25}
              rows={summary}
              onRowClick={handleSummaryRowClick}
              getRowClassName={(params) => {
                return params.indexRelativeToCurrentPage % 2 === 0
                  ? "even"
                  : "odd";
              }}
              sx={{
                "& .even": {
                  backgroundColor: "#f9f9f9",
                },
                "& .odd": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#e0e0e0 !important",
                },
              }}
              columns={[
                {
                  field: "license_email",
                  headerName: "License Email",
                  flex: 1,
                },
                {
                  field: "category_names",
                  headerName: "Category",
                  flex: 2,
                  valueGetter: (params) =>
                    Array.isArray(params) ? params.join(", ") : "—",
                },
                {
                  field: "subtest_names",
                  headerName: "Subtests",
                  flex: 2,
                  valueGetter: (params) =>
                    Array.isArray(params) ? params.join(", ") : "—",
                },
                { field: "skip_type", headerName: "Skip Type", flex: 2 },
                { field: "count", headerName: "Count", flex: 1 },
              ]}
            />
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1">Lab Reports</Typography>
        {loadingFilings ? (
          <CircularProgress />
        ) : (
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={filings}
              rowHeight={25}
              onRowClick={handleOpenFiling}
              getRowClassName={(params) => {
                return params.indexRelativeToCurrentPage % 2 === 0
                  ? "even"
                  : "odd";
              }}
              sx={{
                "& .even": {
                  backgroundColor: "#f9f9f9",
                },
                "& .odd": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#e0e0e0 !important",
                },
              }}
              columns={TABLE_DAYDATA_COLUMNS}
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={openFilingDialog}
        onClose={handleCloseFilingDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Filing Details
          <Button
            onClick={handleCloseFilingDialog}
            sx={{ float: "right" }}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {loadingLabReport ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
            </Box>
          ) : lab && Object.keys(lab).length > 0 ? (
            <>
              <LabFilingDetails lab={lab} />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FullSkipFrame;
